import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import BulkForecastReview from '@/components/procurement/BulkForecastReview';
import DocumentUploader from '@/components/procurement/DocumentUploader';
import LogReceiptDrawer from '@/components/procurement/LogReceiptDrawer';
import {
  Loader2, X, Package, FileText, Sparkles, Zap, ExternalLink,
  CalendarClock, ClipboardCheck, DollarSign, Building2, Truck,
  AlertTriangle, Wand2, TrendingDown,
} from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};
const STATUS_COL = { DONE: '#1E4E8C', ON_TRACK: '#22C55E', AT_RISK: '#F59E0B', DELAYED: '#EF4444', NOT_STARTED: '#CBD5E1' };

function SlippageTimeline({ projectId, mci }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/mci/control-tower/slippage-timeline/${mci}`)
      .then(r => setData(r.data))
      .catch(() => setData({ events: [] }));
  }, [projectId, mci]);
  if (!data) return null;
  if (!data.events?.length) {
    return (
      <div className="rounded-lg p-3 mb-4 text-[11px]"
        style={{ background: 'var(--tone-green-bg)', border: `1px solid #86EFAC`, color: 'var(--tone-green-text)' }}
        data-testid="slippage-timeline-empty">
        <b>Slippage Timeline:</b> no historical late gates — every closed gate finished on/before planned.
      </div>
    );
  }
  return (
    <div className="rounded-lg p-3 mb-4"
      style={{ background: 'var(--tone-red-bg)', border: `1px solid #FCA5A5` }}
      data-testid="slippage-timeline">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1"
          style={{ color: 'var(--tone-red-text)' }}>
          <TrendingDown className="w-3 h-3" /> Slippage Timeline · {data.eventCount} event(s)
        </div>
        <div className="text-[10px] font-mono font-bold" style={{ color: 'var(--tone-red-text)' }}>
          Cumulative slip: {data.totalSlipDays}d
        </div>
      </div>
      <div className="relative pl-2" style={{ borderLeft: `2px solid #FCA5A5` }}>
        {data.events.map((e, i) => (
          <div key={i} className="mb-1.5 last:mb-0 relative">
            <div className="absolute -left-[9px] top-1 w-2.5 h-2.5 rounded-full"
              style={{ background: e.kind === 'LATE_COMPLETION' ? '#EF4444' : '#F59E0B' }} />
            <div className="flex items-center justify-between pl-2 text-[10px]">
              <div className="flex-1">
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{e.stageGateName}</span>
                <span className="ml-1 px-1 py-[1px] rounded text-[8.5px] font-bold"
                  style={{ background: 'var(--tone-red-bg)', color: 'var(--tone-red-text)' }}>{e.phase}</span>
                <span className="ml-1 px-1 py-[1px] rounded text-[8.5px] font-bold"
                  style={{
                    background: e.kind === 'LATE_COMPLETION' ? 'var(--tone-red-bg)' : 'var(--tone-amber-bg)',
                    color: e.kind === 'LATE_COMPLETION' ? 'var(--tone-red-text)' : 'var(--tone-amber-text)',
                  }}>
                  {e.kind === 'LATE_COMPLETION' ? 'CLOSED LATE' : 'FORECAST SLIP'}
                </span>
              </div>
              <div className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>
                planned {e.plannedDate?.slice(0, 10)} · {e.kind === 'LATE_COMPLETION' ? 'actual' : 'forecast'} {(e.actualDate || e.forecastDate)?.slice(0, 10)}
              </div>
              <div className="ml-2 font-mono font-bold" style={{ color: '#EF4444', minWidth: 44, textAlign: 'right' }}>
                +{e.slipDays}d
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function RecoveryButton({ gate, projectId, mci, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const fetchSuggestion = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/gate-forecast/${mci}/recovery-forecast`,
        { stageGateId: gate.stageGateId });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Recovery suggest failed'); }
    setBusy(false);
  };
  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      await api.put(`/v1/projects/${projectId}/mci/control-tower/gate-forecast/${mci}`,
        { stageGateId: gate.stageGateId,
           forecastDate: preview.suggestedForecast, cascade: true });
      toast.success(`Recovery applied · ${preview.cascadeCount} downstream gate(s) cascaded`);
      setPreview(null); onApplied?.();
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    setApplying(false);
  };
  return (
    <>
      <button onClick={fetchSuggestion} disabled={busy}
        className="ml-1 inline-flex items-center gap-0.5 px-1 py-[1px] rounded text-[8.5px] font-bold text-white"
        style={{ background: '#F26B1F' }}
        data-testid={`recover-${gate.stageGateId}`}
        title="Cognara suggests a recovered forecast date + cascade preview">
        {busy ? <Loader2 className="w-2 h-2 animate-spin" /> : <><Wand2 className="w-2 h-2" />Recover</>}
      </button>
      {preview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{ background: 'rgba(15,23,42,0.55)' }}
          onClick={() => setPreview(null)} data-testid="recovery-modal">
          <div className="bg-[var(--card-bg)] rounded-lg shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center gap-2"
              style={{ borderColor: 'rgba(15,23,42,0.10)' }}>
              <Sparkles className="w-4 h-4" style={{ color: '#F26B1F' }} />
              <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Cognara Recovery Forecast</div>
              <button className="ml-auto" onClick={() => setPreview(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 text-[11px] space-y-3">
              <div>
                <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Gate</div>
                <div className="font-bold" style={{ color: 'var(--tone-navy-text)' }}>{preview.stageGateName}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded border" style={{ borderColor: 'rgba(15,23,42,0.10)' }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Current forecast</div>
                  <div className="font-mono font-bold" style={{ color: '#EF4444' }}>{preview.currentForecast?.slice(0, 10) || '—'}</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: '#F26B1F', background: 'var(--tone-orange-bg)' }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: 'var(--tone-orange-text)' }}>Recovered forecast</div>
                  <div className="font-mono font-bold" style={{ color: '#F26B1F' }}>{preview.suggestedForecast?.slice(0, 10)}</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: 'rgba(15,23,42,0.10)' }}>
                  <div className="text-[9px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Δ from current</div>
                  <div className="font-mono font-bold" style={{ color: preview.deltaDays < 0 ? '#10B981' : '#EF4444' }}>
                    {preview.deltaDays > 0 ? '+' : ''}{preview.deltaDays}d
                  </div>
                </div>
              </div>
              <div className="p-2 rounded text-[10px]"
                style={{ background: 'var(--tone-orange-bg)', border: '1px solid #FED7AA', color: 'var(--tone-orange-text)' }}>
                <b>Rationale:</b> {preview.reason}
                <div className="mt-1"><b>Median slip observed:</b> {preview.medianSlipObserved}d · <b>Compressed slip applied:</b> {preview.compressedSlipApplied}d · <b>RAS:</b> {preview.requiredAtSiteDate?.slice(0, 10) || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
                  Downstream cascade preview · {preview.cascadeCount} gate(s)
                </div>
                {preview.cascadeCount === 0 && (
                  <div className="text-[10px] italic p-2 rounded" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                    No downstream open gates — this gate is terminal.
                  </div>
                )}
                {preview.cascadeCount > 0 && (
                  <table className="w-full text-[10px]">
                    <thead style={{ background: 'var(--card-bg-alt)' }}>
                      <tr>
                        <th className="text-left p-1" style={{ color: 'var(--text-muted)' }}>Gate</th>
                        <th className="text-right p-1" style={{ color: 'var(--text-muted)' }}>Current fc</th>
                        <th className="text-right p-1" style={{ color: 'var(--text-muted)' }}>New fc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.cascadePreview.map(p => (
                        <tr key={p.stageGateId} style={{ borderTop: '1px solid rgba(15,23,42,0.10)' }}>
                          <td className="p-1" style={{ color: 'var(--text-primary)' }}>{p.stageGateName}</td>
                          <td className="p-1 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{p.currentForecast?.slice(0, 10) || '—'}</td>
                          <td className="p-1 text-right font-mono" style={{ color: '#F26B1F' }}>{p.newForecast?.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'rgba(15,23,42,0.10)' }}>
                <Button size="sm" variant="outline" onClick={() => setPreview(null)}>Dismiss</Button>
                <Button size="sm" onClick={apply} disabled={applying}
                  className="text-white" style={{ background: '#F26B1F' }}
                  data-testid="recovery-apply">
                  {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                  Apply Recovery + Cascade
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function DateField({ gate, projectId, mci, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState((gate.forecastDate || '').slice(0, 10));
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const disabled = !!gate.actualDate;

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/gate-forecast/${mci}/cognara-suggest`);
      const mine = (r.data.suggestions || []).find(s => s.stageGateId === gate.stageGateId);
      if (mine) {
        setSuggestion(mine);
        setVal(mine.suggestedForecast.slice(0, 10));
        toast.info(`Cognara suggests ${mine.suggestedForecast.slice(0, 10)} · ${mine.reason}`, { duration: 6000 });
      }
    } catch (e) { toast.error('Cognara suggest failed'); }
    setSuggesting(false);
  };
  const save = async () => {
    const todayIso = new Date().toISOString().slice(0, 10);
    if (val <= todayIso) return toast.error('Forecast must be strictly in the future');
    setSaving(true);
    try {
      const { data } = await api.put(`/v1/projects/${projectId}/mci/control-tower/gate-forecast/${mci}`,
        { stageGateId: gate.stageGateId, forecastDate: `${val}T00:00:00Z`, cascade: true });
      const cascaded = data?.cascadedGates || 0;
      const delta = data?.deltaDays || 0;
      if (cascaded > 0) {
        toast.success(`Forecast updated · Cascaded ${cascaded} downstream gate${cascaded === 1 ? '' : 's'} by ${delta > 0 ? '+' : ''}${delta}d`,
          { duration: 6000 });
      } else {
        toast.success('Forecast updated');
      }
      setEditing(false); onSaved?.();
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setSaving(false);
  };
  if (disabled) return <span className="font-mono text-[10px]" style={{ color: C.slateMuted }}>{gate.actualDate?.slice(0, 10)}</span>;
  return editing ? (
    <div className="flex items-center gap-1 flex-wrap">
      <input type="date" value={val} onChange={e => setVal(e.target.value)}
        className="text-[10px] px-1 py-0.5 rounded border" style={{ borderColor: C.border, background: C.surfaceAlt }} />
      <Button size="sm" onClick={save} disabled={saving} className="h-5 text-[9px] px-1.5 text-white" style={{ background: C.primaryText }}
        data-testid={`fc-save-${gate.stageGateId}`}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-5 text-[9px] px-1.5">Cancel</Button>
      <Button size="sm" onClick={suggest} disabled={suggesting} className="h-5 text-[9px] px-1.5 text-white" style={{ background: C.accent }}
        data-testid={`cognara-suggest-${gate.stageGateId}`}>
        {suggesting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <><Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />Cognara</>}
      </Button>
      {suggestion && <span className="text-[9px]" style={{ color: C.accent }}>{suggestion.reason}</span>}
    </div>
  ) : (
    <button className="font-mono text-[10px] underline decoration-dotted" style={{ color: C.primaryText }}
      onClick={() => setEditing(true)} data-testid={`fc-edit-${gate.stageGateId}`}>
      {gate.forecastDate?.slice(0, 10) || '—'}
    </button>
  );
}

export default function MciPackageDrawer({ projectId, mci, onClose }) {
  const [data, setData] = useState(null);
  const [bulkRows, setBulkRows] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);
  const load = () => api.get(`/v1/projects/${projectId}/mci/control-tower/vendor-performance/${mci}`)
    .then(r => setData(r.data))
    .catch(() => { toast.error('Failed to load drilldown'); onClose(); });
  useEffect(() => { if (mci) load(); /* eslint-disable-next-line */ }, [mci, projectId]);
  if (!mci) return null;

  const runBulkSuggest = async () => {
    setBulkBusy(true);
    try {
      const { data: r } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/gate-forecast/${mci}/cognara-suggest`);
      if (!r.suggestions?.length) {
        toast.info('No open gates to forecast — all planned dates are already actualised or blank.');
        setBulkBusy(false); return;
      }
      // Reshape into BulkForecastReview row-format (single MCI)
      setBulkRows([{
        mciLineNumber: mci,
        materialDescription: data?.materialDescription,
        vendor: data?.vendor?.name, priority: data?.priority,
        suggestions: (r.suggestions || []).map(s => ({
          ...s, phase: s.phase === 'preOrderProgress' ? 'PRE' : 'POST',
        })),
      }]);
    } catch (e) { toast.error('Cognara suggest failed'); }
    setBulkBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} data-testid="mci-drawer">
      <div className="absolute right-0 top-0 h-full w-full max-w-3xl overflow-y-auto p-5"
        onClick={e => e.stopPropagation()} style={{ background: C.surface, borderLeft: `1px solid ${C.border}` }}>
        {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto mt-10" /> : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.primaryText }}>
                  <Package className="w-3 h-3 inline mr-1" />{data.materialCategory} · {data.discipline} · {data.area}
                </div>
                <div className="font-mono font-bold text-lg" style={{ color: C.primaryText }}>{data.mciLineNumber}</div>
                <div className="text-sm" style={{ color: C.slate }}>{data.materialDescription}</div>
                <div className="text-[10px] mt-0.5" style={{ color: C.slateMuted }}>Priority: <b style={{ color: data.priority === 'CRITICAL' ? C.accent : C.slate }}>{data.priority}</b></div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={runBulkSuggest} disabled={bulkBusy}
                  className="h-7 text-[11px] text-white font-semibold px-3" style={{ background: '#F26B1F' }}
                  data-testid="cognara-forecast-all-gates-btn">
                  {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> :
                    <><Sparkles className="w-3 h-3 mr-1" />Cognara — Forecast All Open Gates</>}
                </Button>
                <button onClick={onClose}><X className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Vendor scorecard */}
            <div className="rounded-lg p-3 mb-4" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="mci-vendor-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1" style={{ color: C.primaryText }}>
                  <Building2 className="w-3 h-3" /> Vendor · {data.vendor?.name || '— (not yet awarded)'}
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded font-bold"
                  style={{ background: (data.vendor?.stats?.otdPercent ?? 0) < 60 ? '#EF4444' : (data.vendor?.stats?.otdPercent ?? 0) < 85 ? C.accent : '#22C55E', color: '#fff' }}>
                  OTD {data.vendor?.stats?.otdPercent ?? 0}%
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-[10px]">
                <div><div style={{ color: C.slateMuted }}>Total POs</div><div className="text-base font-bold" style={{ color: C.slate }}>{data.vendor?.stats?.totalPOs ?? 0}</div></div>
                <div><div style={{ color: C.slateMuted }}>On-Time</div><div className="text-base font-bold" style={{ color: '#22C55E' }}>{data.vendor?.stats?.onTime ?? 0}</div></div>
                <div><div style={{ color: C.slateMuted }}>Delayed</div><div className="text-base font-bold" style={{ color: '#EF4444' }}>{data.vendor?.stats?.delayed ?? 0}</div></div>
                <div><div style={{ color: C.slateMuted }}>Total Value</div><div className="text-base font-bold" style={{ color: C.accent }}>${Number(data.vendor?.stats?.totalValue || 0).toLocaleString()}</div></div>
              </div>
            </div>

            {/* PO + Documents */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg p-3" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="mci-po-card">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase font-semibold flex items-center gap-1" style={{ color: C.primaryText }}>
                    <ClipboardCheck className="w-3 h-3" /> Purchase Order
                  </div>
                  <Button size="sm" onClick={() => setShowReceipts(true)}
                    className="h-5 text-[9px] text-white" style={{ background: C.accent }}
                    data-testid="open-log-receipt-btn"
                    title="Log GRN + IQC + Rejected qty against this PO line">
                    <FileText className="w-2.5 h-2.5 mr-1" /> Log Receipt
                  </Button>
                </div>
                <div className="text-[11px] space-y-0.5" style={{ color: C.slate }}>
                  <div>PO: <b>{data.po?.poNumber || '—'}</b> · Line Item: <b style={{ color: '#F26B1F' }}>L{data.po?.poLineItem || '—'}</b></div>
                  <div>Date: {data.po?.poDate?.slice(0, 10) || '—'}</div>
                  <div>Value: <b style={{ color: C.accent }}>${Number(data.po?.poValue || 0).toLocaleString()} {data.po?.poCurrency}</b></div>
                  {data.po?.poDocumentRef && (
                    <a href={data.po.poDocumentRef} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[10px] underline mt-1" style={{ color: C.primaryText }} data-testid="po-doc-link">
                      <FileText className="w-3 h-3" /> Open PO Document <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                  <div className="mt-1">
                    <DocumentUploader projectId={projectId}
                      currentUrl={data.po?.poDocumentRef}
                      label={data.po?.poDocumentRef ? 'Replace PO Doc' : 'Attach PO Doc'}
                      testId="po-doc-uploader"
                      onUploaded={async (url) => {
                        try {
                          await api.patch(`/v1/projects/${projectId}/mci/entries/${mci}/po-document`, { poDocumentRef: url });
                          load();
                        } catch (e) { /* toast handled by uploader */ }
                      }} />
                  </div>
                </div>
              </div>
              <div className="rounded-lg p-3" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="mci-docs-card">
                <div className="text-[10px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ color: C.primaryText }}>
                  <FileText className="w-3 h-3" /> Documents
                </div>
                <div className="text-[11px] space-y-1" style={{ color: C.slate }}>
                  {data.documents?.mrDocument && <div><a href={data.documents.mrDocument} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: C.primaryText }}><FileText className="w-3 h-3" /> MR Document</a></div>}
                  {data.documents?.vendorDrawing && <div><a href={data.documents.vendorDrawing} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: C.primaryText }} data-testid="vdr-link"><FileText className="w-3 h-3" /> Vendor Drawing (VDR)</a></div>}
                  {!data.documents?.mrDocument && !data.documents?.vendorDrawing && <span style={{ color: C.slateMuted }}>No documents linked yet.</span>}
                </div>
              </div>
            </div>

            {/* Slippage Timeline — historical late/delayed events */}
            <SlippageTimeline projectId={projectId} mci={mci} />

            {/* Stage gates with inline forecast picker */}
            {['preOrder', 'postOrder'].map(phase => (
              <div key={phase} className="mb-4" data-testid={`stage-list-${phase}`}>
                <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2 flex items-center gap-1" style={{ color: C.accent }}>
                  <CalendarClock className="w-3 h-3" /> {phase === 'preOrder' ? 'Pre-Order Gates' : 'Post-Order Gates'}
                </div>
                <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
                  <table className="w-full text-[11px]">
                    <thead style={{ background: `${C.primary}08` }}>
                      <tr>
                        <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Gate</th>
                        <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Planned</th>
                        <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Forecast <span className="text-[9px] normal-case">(click to edit)</span></th>
                        <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Actual</th>
                        <th className="p-1.5 text-center" style={{ color: C.slateMuted }}>Status</th>
                        <th className="p-1.5 text-center" style={{ color: C.slateMuted }}>Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.stageGates?.[phase] || []).map((g, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${C.border}` }} data-testid={`gate-row-${g.stageGateId}`}>
                          <td className="p-1.5" style={{ color: C.slate }}>{g.stageGateName}</td>
                          <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: C.slateMuted }}>{g.plannedDate?.slice(0, 10) || '—'}</td>
                          <td className="p-1.5 text-right">
                            <DateField gate={g} projectId={projectId} mci={mci} onSaved={load} />
                          </td>
                          <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: C.primaryText }}>{g.actualDate?.slice(0, 10) || '—'}</td>
                          <td className="p-1.5 text-center">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: STATUS_COL[g.status] || 'var(--text-muted)' }}>{g.status?.replace('_', ' ')}</span>
                            {g.status === 'DELAYED' && !g.actualDate && (
                              <RecoveryButton gate={g} projectId={projectId} mci={mci} onApplied={load} />
                            )}
                          </td>
                          <td className="p-1.5 text-center">
                            {g.documentRef && <a href={g.documentRef} target="_blank" rel="noreferrer" style={{ color: C.primaryText }} title={g.documentRef}><FileText className="w-3 h-3 inline" /></a>}
                            {g.inspectionReportRef && <a href={g.inspectionReportRef} target="_blank" rel="noreferrer" className="ml-1" style={{ color: C.accent }} title="Inspection Report"><ClipboardCheck className="w-3 h-3 inline" /></a>}
                            {g.shippingTrackingRef && <a href={`https://track.aftership.com/${g.shippingTrackingRef}`} target="_blank" rel="noreferrer" className="ml-1" style={{ color: '#0EA5E9' }} title={`Tracking: ${g.shippingTrackingRef}`}><Truck className="w-3 h-3 inline" /></a>}
                            <div className="mt-1">
                              <DocumentUploader projectId={projectId}
                                currentUrl={g.documentRef}
                                label="Doc"
                                testId={`gate-doc-${g.stageGateId}`}
                                onUploaded={async (url) => {
                                  try {
                                    await api.patch(
                                      `/v1/projects/${projectId}/mci/entries/${mci}/gates/${g.stageGateId}/document`,
                                      { documentRef: url });
                                    load();
                                  } catch (err) { /* toast handled */ }
                                }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <div className="rounded-lg p-2 text-[10px]" style={{ background: `${C.accent}12`, border: `1px dashed ${C.accent}` }}>
              <b style={{ color: C.accent }}>Human-in-loop:</b> forecast dates can be Cognara-suggested per gate (uses observed slip on DONE gates + category typical slip), but the user must confirm each edit. Past dates are rejected server-side. DONE gates cannot be edited (forecast = actual).
            </div>
          </>
        )}
      </div>
      {bulkRows && (
        <BulkForecastReview projectId={projectId} rows={bulkRows}
          title={`Cognara Bulk Forecast — ${mci}`}
          onClose={() => setBulkRows(null)}
          onApplied={() => { load(); }} />
      )}
      {showReceipts && (
        <LogReceiptDrawer projectId={projectId} mci={mci}
          unit={data?.entry?.unit || data?.po?.unit || data?.entry?.unitOfMeasure}
          scope={data?.entry?.quantity || 0}
          defaultLocation={data?.entry?.defaultReceiptLocation}
          onClose={() => setShowReceipts(false)}
          onChanged={() => load()} />
      )}
    </div>
  );
}
